// Arbre de mise en page (docs/EDITEUR-MISE-EN-PAGE.md §1 et §4) : validation d'un arbre venu de la
// base et construction à la volée depuis les anciens champs d'une charte.
import type {
  BlockConfig,
  BlockId,
  Brand,
  FieldId,
  LayoutBlock,
  LayoutBorder,
  LayoutCol,
  LayoutRow,
  Padding,
  SignatureLayout,
} from './types.ts';
import { layoutFromBlocks, normalizeBlocks } from './blocks.ts';

export const LAYOUT_VERSION = 1;
export const DEFAULT_LAYOUT_WIDTH = 600;
/** Imbrication maximale de colonnes (au-delà, Outlook devient imprévisible). */
export const MAX_COLUMN_DEPTH = 2;

const FIELD_IDS: readonly FieldId[] = ['name', 'title', 'phone_mobile', 'phone_office', 'email', 'address', 'website', 'legal'];
/** Anciens champs (avant le 21/09/2026) : « Fonction — Lieu » redevient Fonction, le lieu seul disparaît. */
const FIELD_ALIASES: Record<string, FieldId | 'drop'> = { title_venue: 'title', venue_title: 'title', venue: 'drop' };
const BLOCK_TYPES = ['logo', 'field', 'social', 'banner', 'text', 'image', 'spacer', 'divider'] as const;
const NETWORK_IDS = ['web', 'instagram', 'facebook', 'tiktok', 'linkedin'] as const;
const TEXT_ROLES = ['name', 'title', 'body', 'legal'] as const;

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

function padding(v: unknown): Padding | undefined {
  if (!Array.isArray(v) || v.length !== 4) return undefined;
  const p = v.map((x) => num(x));
  return p.every((x): x is number => x !== undefined) ? [p[0], p[1], p[2], p[3]] : undefined;
}

/** Trait : seule l'épaisseur compte ; une couleur enregistrée par une version antérieure est ignorée. */
function border(v: unknown): LayoutBorder | undefined {
  if (!isObj(v)) return undefined;
  const width = num(v.width);
  return width !== undefined && width > 0 ? { width } : undefined;
}

const align = (v: unknown): 'left' | 'center' | 'right' | undefined => (v === 'center' || v === 'right' || v === 'left' ? v : undefined);

/**
 * Un bloc : structure seulement. Les propriétés de style d'un arbre enregistré avant la refonte
 * (color, fontSize, fontWeight, fontStyle, align, lineHeight, letterSpacing, networks, size, gap)
 * sont IGNORÉES en silence (docs/REFONTE-CHARTES.md §1.3) : le style vient du rôle de la charte.
 */
function block(v: unknown): LayoutBlock | null | 'drop' {
  if (!isObj(v)) return null;
  const type = str(v.type);
  const id = str(v.id) ?? '';
  if (!type || !(BLOCK_TYPES as readonly string[]).includes(type) || !id) return null;
  const pad = padding(v.padding);
  const href = str(v.href);
  const base = { id, ...(pad ? { padding: pad } : {}), ...(href ? { href } : {}) };
  switch (type) {
    case 'logo':
    case 'banner':
      return { ...base, type };
    case 'social': {
      // icônes retirées dans l'éditeur : liste des réseaux gardés (identifiants inconnus ignorés) ; absente = toutes
      const networks = Array.isArray(v.networks) ? v.networks.filter((n): n is (typeof NETWORK_IDS)[number] => (NETWORK_IDS as readonly string[]).includes(n as string)) : undefined;
      return { ...base, type, ...(networks ? { networks } : {}) };
    }
    case 'field': {
      const raw = str(v.field) ?? '';
      const field = FIELD_ALIASES[raw] ?? raw;
      if (field === 'drop') return 'drop'; // bloc « Nom du lieu » d'un ancien arbre : retiré en silence
      return (FIELD_IDS as readonly string[]).includes(field) ? { ...base, type, field: field as FieldId } : null;
    }
    case 'text': {
      const raw = typeof v.role === 'string' ? v.role : '';
      const role = (TEXT_ROLES as readonly string[]).includes(raw) || /^x-[a-z0-9]{4,12}$/.test(raw) ? raw : undefined;
      return { ...base, type, text: str(v.text) ?? '', ...(role ? { role } : {}) };
    }
    case 'image': {
      const url = str(v.url);
      const width = num(v.width);
      const height = num(v.height);
      return url && width && height ? { ...base, type, url, width, height } : null;
    }
    case 'spacer':
      return { ...base, type, height: num(v.height) ?? 8 };
    case 'divider': {
      const width = num(v.width);
      return { ...base, type, ...(width ? { width } : {}) };
    }
  }
  return null;
}

function col(v: unknown, depth: number): LayoutCol | null {
  if (!isObj(v) || !Array.isArray(v.blocks)) return null;
  const width = typeof v.width === 'number' ? v.width : str(v.width) ?? 'auto';
  // Un bloc inconnu ou incomplet invalide la colonne, donc l'arbre : mieux vaut le repli qu'un rendu amputé.
  const parsedBlocks = v.blocks.map(block);
  if (parsedBlocks.some((b) => b === null)) return null;
  const blocks = parsedBlocks.filter((b): b is LayoutBlock => b !== 'drop');
  // Sous-lignes : au-delà de MAX_COLUMN_DEPTH niveaux de colonnes, l'arbre entier est refusé (Outlook) ;
  // une sous-ligne invalide invalide la colonne, donc l'arbre.
  let rows: LayoutRow[] | undefined;
  if (Array.isArray(v.rows) && v.rows.length) {
    if (depth + 1 > MAX_COLUMN_DEPTH) return null;
    const parsed = v.rows.map((r) => row(r, depth + 1));
    if (parsed.some((r) => r === null)) return null;
    rows = parsed as LayoutRow[];
  }
  return {
    id: str(v.id) ?? '',
    width,
    valign: v.valign === 'middle' || v.valign === 'bottom' ? v.valign : 'top',
    align: align(v.align),
    padding: padding(v.padding),
    borderLeft: border(v.borderLeft),
    borderRight: border(v.borderRight),
    borderTop: border(v.borderTop),
    borderBottom: border(v.borderBottom),
    backgroundColor: str(v.backgroundColor),
    blocks,
    rows,
  };
}

function row(v: unknown, depth: number): LayoutRow | null {
  if (!isObj(v) || !Array.isArray(v.cols)) return null;
  const cols = v.cols.map((c) => col(c, depth)).filter((c): c is LayoutCol => c !== null);
  if (v.cols.length && cols.length !== v.cols.length) return null; // une colonne invalide invalide la ligne
  return { id: str(v.id) ?? '', cols };
}

/**
 * Arbre exploitable, ou null si la valeur est absente, corrompue, d'une version inconnue ou trop
 * profonde. Jamais d'exception : l'appelant se replie sur layoutFromLegacy().
 */
export function normalizeLayout(input: unknown): SignatureLayout | null {
  if (!isObj(input) || input.version !== LAYOUT_VERSION || !Array.isArray(input.rows)) return null;
  const rows = input.rows.map((r) => row(r, 1));
  if (rows.some((r) => r === null)) return null;
  const width = num(input.width);
  return { version: 1, width: width && width > 0 ? Math.round(width) : DEFAULT_LAYOUT_WIDTH, rows: rows as LayoutRow[] };
}

// ---------------------------------------------------------------- conversion depuis les anciens champs

const px = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.round(v)) : fallback);

/** Un bloc `field`, ou le bloc texte « {{fonction}} — {{lieu}} » quand fonction et lieu se suivent. */
function fieldBlock(id: string, field: FieldId, pad?: Padding): LayoutBlock {
  return { id, type: 'field', field, ...(pad ? { padding: pad } : {}) };
}

const CONTACT_FIELDS: readonly BlockId[] = ['phone_mobile', 'phone_office', 'email', 'address', 'website'];

/**
 * Blocs de la colonne de texte à partir des blocs visibles, en reproduisant l'espacement de l'ancien
 * moteur : fonction — lieu sur une ligne (marge basse 6 px en classique), premier groupe de contacts
 * collé, groupes suivants avec une marge haute (6 px classique, 4 px compact).
 */
function textBlocks(visible: BlockConfig[], compact: boolean): LayoutBlock[] {
  const out: LayoutBlock[] = [];
  const ids = visible.map((b) => b.id);
  const gap: Padding = [compact ? 4 : 6, 0, 0, 0];
  let groups = 0;
  const groupPad = () => (compact || groups > 0 ? gap : undefined);
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    if (id === 'name') out.push(fieldBlock(`b-${id}`, 'name'));
    else if (id === 'title' || id === 'venue') {
      // Le nom du lieu ne fait plus partie de la signature (21/09/2026) : l'ancienne paire
      // « fonction — lieu » (dans un ordre ou l'autre) devient le seul bloc Fonction ; un lieu seul disparaît.
      const next = ids[i + 1];
      const merged = (id === 'title' && next === 'venue') || (id === 'venue' && next === 'title');
      const pad: Padding | undefined = compact ? undefined : [0, 0, 6, 0];
      if (merged) i++;
      if (merged || id === 'title') out.push(fieldBlock('b-title', 'title', pad));
    } else if (CONTACT_FIELDS.includes(id)) {
      let first = true;
      let j = i;
      for (; j < ids.length && CONTACT_FIELDS.includes(ids[j]); j++) {
        out.push(fieldBlock(`b-${ids[j]}`, ids[j] as FieldId, first ? groupPad() : undefined));
        first = false;
      }
      i = j - 1;
      groups++;
    } else if (id === 'social') {
      out.push({ id: 'b-social', type: 'social', ...(groupPad() ? { padding: groupPad() } : {}) });
      groups++;
    } else if (compact && id === 'banner') out.push({ id: 'b-banner', type: 'banner', padding: [10, 0, 0, 0] });
    else if (compact && id === 'legal') out.push(fieldBlock('b-legal', 'legal', [8, 0, 0, 0]));
  }
  return out;
}

/**
 * Construit l'arbre équivalent aux anciens réglages (layout classic/compact, logo_position, séparateur,
 * blocs). C'est ce que voit une charte tant que layout_tree est null.
 */
export function layoutFromLegacy(brand: Brand): SignatureLayout {
  const { above, text, below } = layoutFromBlocks(brand.blocks);
  const visibleText = text.filter((b) => b.visible);
  const compact = brand.layout === 'compact';
  const rows: LayoutRow[] = [];
  const col = (id: string, blocks: LayoutBlock[], extra: Partial<LayoutCol> = {}): LayoutCol => ({ id, width: 'auto', valign: 'top', blocks, ...extra });

  const fullWidth = (list: BlockConfig[], position: 'above' | 'below') =>
    list.filter((b) => b.visible).forEach((b) => {
      const pad = b.id === 'banner' ? 12 : 8;
      const padding: Padding = position === 'above' ? [0, 0, pad, 0] : [pad, 0, 0, 0];
      const blk: LayoutBlock = b.id === 'banner' ? { id: 'b-banner', type: 'banner' } : fieldBlock('b-legal', 'legal');
      rows.push({ id: `r-${b.id}-${position}`, cols: [col(`c-${b.id}-${position}`, [blk], { padding })] });
    });

  if (compact) {
    // Une seule colonne, tout dans l'ordre brut des blocs (bannière et mention comprises, où qu'elles soient).
    const all = normalizeBlocks(brand.blocks).filter((b) => b.visible);
    rows.push({ id: 'r-main', cols: [col('c-main', textBlocks(all, true))] });
    return { version: 1, width: DEFAULT_LAYOUT_WIDTH, rows };
  }

  fullWidth(above, 'above');

  const sepLine = brand.separator_style !== 'none';
  const sepWidth = px(brand.separator_width, 2);
  const gap = sepLine ? px(brand.separator_gap, 16) : 0;
  const sep: LayoutBorder | undefined = sepLine ? { width: sepWidth } : undefined; // couleur : rôle separator
  const logoWidth = px(brand.logo_width, 145);
  const logo: LayoutBlock = { id: 'b-logo', type: 'logo' };
  const textCol = (extra: Partial<LayoutCol>) => col('c-text', textBlocks(visibleText, false), extra);
  const pos = brand.logo_position === 'right' || brand.logo_position === 'top' ? brand.logo_position : 'left';

  if (pos === 'top') {
    rows.push({ id: 'r-logo', cols: [col('c-logo', [logo], { padding: [0, 0, gap, 0] })] });
    rows.push({ id: 'r-text', cols: [textCol({ padding: [gap, 0, 0, 0], ...(sep ? { borderTop: sep } : {}) })] });
  } else if (pos === 'right') {
    rows.push({
      id: 'r-identity',
      cols: [
        textCol({ padding: [0, gap, 0, 0], ...(sep ? { borderRight: sep } : {}) }),
        col('c-logo', [logo], { width: logoWidth + gap, padding: [0, 0, 0, gap] }),
      ],
    });
  } else {
    rows.push({
      id: 'r-identity',
      cols: [
        col('c-logo', [logo], { width: logoWidth + gap, padding: [0, gap, 0, 0] }),
        textCol({ padding: [0, 0, 0, gap], ...(sep ? { borderLeft: sep } : {}) }),
      ],
    });
  }

  fullWidth(below, 'below');
  return { version: 1, width: DEFAULT_LAYOUT_WIDTH, rows };
}

/** L'arbre effectif d'une charte : celui enregistré s'il est valide, sinon la conversion des anciens champs. */
export function effectiveLayout(brand: Brand): SignatureLayout {
  return normalizeLayout(brand.layout_tree) ?? layoutFromLegacy(brand);
}
